import { useApolloClient } from "@apollo/client";
import { MenuItemWithIcon } from "@msys/ui";
import { FindReplace as FindReplaceIcon } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@tolgee/react";
import { pick } from "lodash-es";
import React, { useState } from "react";
import type {
  CreateItemsValues,
  TplApplicableFor,
} from "../../../../clients/graphqlTypes.js";
import { useUserData } from "../../../auth/useUserData.js";
import { ConfirmModalProps } from "../../../commons/modals/ConfirmProcess.js";
import { TemplatesQuoteSelectMultipleComponent_TemplateFragment } from "../../templates/quote/TemplatesQuoteSelectMultipleModal.generated.js";
import { useDeleteItemMutation } from "../hooks/useDeleteItemMutation.js";
import { useDocItemsClipboard } from "../hooks/useDocItemsClipboard.js";
import { SearchItemInputModal } from "../SearchItemInputModal.js";
import {
  type SearchTab,
  type SelectedProduct,
} from "../SearchProductsAndTemplates.js";
import {
  useCreateItemsFromTemplatesMutation,
  useQuoteItemCreateItemsMutation,
} from "./ReplaceWithTemplatesMenuItem.generated.js";

interface Props {
  docId: string;
  projectId: string;
  itemId: string;
  itemRank: number;
  parentItemId: string;
  applicableFor?: TplApplicableFor[];
  disabled?: boolean;
  onItemCreated?: () => Promise<void> | void;
  onItemDeleted?: () => Promise<void> | void;
  startConfirmProcess: (
    props: ConfirmModalProps
  ) => Promise<boolean | undefined>;
  expandedItemIds: string[] | undefined;
}

export const ReplaceWithTemplatesMenuItem = ({
  docId,
  projectId,
  itemId,
  itemRank,
  parentItemId,
  applicableFor,
  disabled,
  onItemCreated,
  onItemDeleted,
  startConfirmProcess,
  expandedItemIds,
}: Props) => {
  const { t } = useTranslate(["QuoteItem", "Global"]);
  const client = useApolloClient();
  const { hasOrganisationPermission } = useUserData();
  // The product-lists tab's queries need `pim_catalogue_update`, which a quote
  // editor does not necessarily hold, so the tab is whitelisted explicitly
  // instead of relying on the modal's "no whitelist means every tab" default.
  const allowedSearchTabs = React.useMemo<SearchTab[]>(
    () =>
      hasOrganisationPermission("pim_catalogue_update")
        ? ["products", "templates", "productLists"]
        : ["products", "templates"],
    [hasOrganisationPermission]
  );
  const [createQuoteItems] = useQuoteItemCreateItemsMutation({ client });
  const [createItemsFromTemplates] = useCreateItemsFromTemplatesMutation({
    client,
  });
  const {
    deleteItem,
    deleteItemLoading,
    checkItemBeforeDelete,
    checkItemCouldBeDeletedLoading,
  } = useDeleteItemMutation(expandedItemIds);

  const [content, setContent] = useDocItemsClipboard();
  const isRemovingItemFromClipboard =
    content?.projectId === projectId &&
    content?.docId === docId &&
    content?.itemId === itemId;

  const [creatingItem, setCreatingItem] = useState<boolean>(false);

  const [searchModalState, setSearchModalState] = React.useState<{
    searchValue: string;
    searchTab: SearchTab;
  } | null>(null);

  const createItemsFromTemplatesWithUnnesting = async (
    templates: {
      quantity: number;
      template: TemplatesQuoteSelectMultipleComponent_TemplateFragment;
    }[]
  ) => {
    await createItemsFromTemplates({
      variables: {
        input: {
          parentProjectId: projectId,
          parentDocId: docId,
          parentItemId,
          templates: templates.map(tpl => ({
            templateQuoteId: tpl.template.id,
            templateEstimatedQuantity: tpl.quantity,
          })),
          rank: itemRank + 1,
        },
      },
    });
  };

  const createItemsWithProducts = async (
    products: {
      quantity: number;
      product: SelectedProduct;
    }[]
  ) => {
    const items: CreateItemsValues[] = products.map(opts =>
      "__typename" in opts.product
        ? {
            title: opts.product.texts?.title ?? "",
            type: "paid",
            decisionBehaviorOfSubitems: "NONE",
            estimatedQuantity: opts.quantity,
            product: pick(opts.product, "supplierId", "articleNumber"),
          }
        : {
            title: opts.product.productTitle ?? "",
            type: "paid",
            decisionBehaviorOfSubitems: "NONE",
            estimatedQuantity: opts.quantity,
            productValues: opts.product,
          }
    );
    const result = await createQuoteItems({
      variables: {
        input: {
          projectId,
          docId,
          parentItemId,
          items,
          rank: itemRank + 1,
        },
      },
    });
  };

  const checkItemCanBeDeleted = async () => {
    if (isRemovingItemFromClipboard) {
      const result = await startConfirmProcess({
        title: t("Confirm replacement", {
          ns: "Global",
        }),
        text: t(
          "You have copied this item to the clipboard. You will not be able to paste it if you replace it. Would you like to continue?",
          {
            ns: "QuoteItem",
          }
        ),
        confirmButtonLabel: t("Replace item", {
          ns: "QuoteItem",
        }),
      });
      if (!result) return false;
    }

    const { itemOrSubItemsPropsUsedInOtherItems } = await checkItemBeforeDelete(
      { projectId, docId, itemId }
    );
    if (itemOrSubItemsPropsUsedInOtherItems) {
      const result = await startConfirmProcess({
        title: t("Confirm replacement", {
          ns: "Global",
        }),
        text: t(
          "Some formulas will be lost when replacing this item. Would you like to continue?",
          {
            ns: "QuoteItem",
          }
        ),
        confirmButtonLabel: t("Replace item", {
          ns: "QuoteItem",
        }),
      });
      if (!result) return false;
    }

    return true;
  };

  return (
    <>
      <MenuItemWithIcon
        icon={<FindReplaceIcon />}
        disabled={
          disabled ||
          creatingItem ||
          deleteItemLoading ||
          checkItemCouldBeDeletedLoading
        }
        onClick={() => {
          setSearchModalState({
            searchValue: "",
            searchTab: "templates",
          });
        }}
      >
        {t("Replace with template", {
          ns: "QuoteItem",
        })}
      </MenuItemWithIcon>
      {searchModalState && (
        <SearchItemInputModal
          initialSearchValue={searchModalState.searchValue}
          initialSearchTab={searchModalState.searchTab}
          allowedSearchTabs={allowedSearchTabs}
          handleClose={async () => {
            setSearchModalState(null);
          }}
          excludeTemplateIds={[docId]}
          applicableFor={applicableFor}
          handleSelect={async ({ products, templates }) => {
            if (creatingItem) return;
            if (!(await checkItemCanBeDeleted())) return;
            try {
              setCreatingItem(true);
              if (products.length > 0) await createItemsWithProducts(products);
              if (templates.length > 0)
                await createItemsFromTemplatesWithUnnesting(templates);
              await onItemCreated?.();
              if (deleteItemLoading) return;
              await deleteItem({
                projectId,
                docId,
                itemId,
                onItemDeleted,
              });
            } catch (e) {
              console.error(e);
              Sentry.captureException(e);
            } finally {
              setCreatingItem(false);
            }
          }}
        />
      )}
    </>
  );
};
